import Player from '@vimeo/player';
import React, { useRef, useState, useEffect } from 'react';

import withLoveHeroTrailer from 'src/client/assets/images/withLove/with-love-hero-trailer.mp4';
import withLoveThumbnail from 'src/client/assets/images/withLove/with-love-thumbnail.png';

import { WITH_LOVE_VIMEO_URL } from '../../constants';

import * as S from './styles';

function VideoPlayer() {
  const containerRef = useRef<HTMLDivElement>(null);
  const vimeoRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<Player | null>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);

  const [isPlayingFull, setIsPlayingFull] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [isHoveringPlay, setIsHoveringPlay] = useState(false);
  const [isHoveringCta, setIsHoveringCta] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInView(entry.isIntersecting);
        });
      },
      { threshold: 0.9 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (previewVideoRef.current && !isPlayingFull) {
      if (isInView) {
        previewVideoRef.current.play().catch(() => {
          console.error('Failed to autoplay preview video');
        });
      } else {
        previewVideoRef.current.pause();
      }
    }
  }, [isInView, isPlayingFull]);

  useEffect(() => {
    if (isPlayingFull && vimeoRef.current && !playerRef.current) {
      playerRef.current = new Player(vimeoRef.current);

      playerRef.current.on('pause', () => {
        setShowOverlay(true);
      });

      playerRef.current.on('play', () => {
        setShowOverlay(false);
      });

      playerRef.current.on('ended', () => {
        setShowOverlay(true);
      });
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [isPlayingFull]);

  const handlePlayClick = () => {
    if (!isPlayingFull) {
      setIsPlayingFull(true);
      setShowOverlay(false);
    } else if (playerRef.current) {
      playerRef.current.play();
      setShowOverlay(false);
    }
  };

  return (
    <S.VideoContainer ref={containerRef}>
      {isPlayingFull ? (
        <S.IFrame
          allowFullScreen
          isVimeo
          allow="autoplay; fullscreen; picture-in-picture"
          ref={vimeoRef}
          src={WITH_LOVE_VIMEO_URL}
        />
      ) : isInView ? (
        <S.PreviewVideo loop muted playsInline ref={previewVideoRef}>
          <source src={withLoveHeroTrailer} type="video/mp4" />
        </S.PreviewVideo>
      ) : (
        <S.ThumbnailImage alt="Video thumbnail" src={withLoveThumbnail} />
      )}

      {showOverlay && (
        <S.Overlay>
          <S.VideoControlContainer>
            <S.CenterContent>
              <S.PlayButtonContainer
                isHovering={isHoveringPlay}
                onClick={handlePlayClick}
                onMouseEnter={() => setIsHoveringPlay(true)}
                onMouseLeave={() => setIsHoveringPlay(false)}
              >
                <S.PlayIcon />
              </S.PlayButtonContainer>

              <S.CtaText
                isHovering={isHoveringCta}
                onMouseEnter={() => setIsHoveringCta(true)}
                onMouseLeave={() => setIsHoveringCta(false)}
              >
                Watch the Film Made for You
              </S.CtaText>

              <S.VideoInfo>
                Sound on recommended · CC available
                <br />5 min · 3 parts
              </S.VideoInfo>
            </S.CenterContent>
          </S.VideoControlContainer>

          <S.BrandSection>
            <S.BrandTitle>WITH LOVE</S.BrandTitle>
            <S.BrandDescription>
              Across organizations and communities,
              <br />
              we share this film as a message to you, and as a reminder to
              ourselves.
            </S.BrandDescription>
          </S.BrandSection>
        </S.Overlay>
      )}
    </S.VideoContainer>
  );
}

export default VideoPlayer;
